import { z } from "zod";

const TextNodeSchema = z.object({
  type: z.literal("text"),
  text: z.string().optional(),
});

const HardBreakNodeSchema = z.object({
  type: z.literal("hardBreak"),
});

const MentionNodeSchema = z.object({
  type: z.literal("mention"),
  attrs: z.object({
    id: z.string().optional(),
  }),
});

const ParagraphNodeSchema = z.object({
  type: z.literal("paragraph"),
  content: z
    .array(z.union([TextNodeSchema, MentionNodeSchema, HardBreakNodeSchema]))
    .optional(),
});

const HeadingNodeSchema = z.object({
  type: z.literal("heading"),
  attrs: z.object({
    level: z.number(),
  }),
  content: z
    .array(z.union([TextNodeSchema, MentionNodeSchema, HardBreakNodeSchema]))
    .optional(),
});

export const TiptapNodeSchema = z.union([
  TextNodeSchema,
  MentionNodeSchema,
  ParagraphNodeSchema,
  HardBreakNodeSchema,
  HeadingNodeSchema,
]);

export const TiptapDocSchema = z.object({
  type: z.literal("doc"),
  content: z.array(TiptapNodeSchema),
});

/**
 * A string that can be templated to reference other nodes and models
 */
export const TemplatedTextSchema = z.union([z.string(), TiptapDocSchema]);
export type TemplatedText = z.infer<typeof TemplatedTextSchema>;
export type TiptapDoc = z.infer<typeof TiptapDocSchema>;
export type TiptapNode = z.infer<typeof TiptapNodeSchema>;

export const templateToString = (template: TemplatedText): string => {
  if (!template) return "";

  if (typeof template === "string") {
    try {
      // Attempt to parse the string as a tiptap doc json
      // if its valid, return that
      return tiptapDocToText(TiptapDocSchema.parse(JSON.parse(template)));
    } catch {
      // otherwise, return the original string
      return template;
    }
  }

  return tiptapDocToText(template);
};

const tiptapDocToText = (doc: TiptapDoc): string => {
  let ret = "";
  const blocks: TiptapNode[] = [...doc.content];

  while (blocks.length > 0) {
    const block = blocks.shift();
    if (!block) continue;

    if (block.type === "paragraph" || block.type === "heading") {
      blocks.push(...(block.content ?? []));
    } else if (block.type === "text") {
      ret += block.text;
    } else if (block.type === "mention" && block.attrs.id) {
      ret += block.attrs.id;
    }
  }

  return ret;
};
